import { IonButton, IonCol, IonContent, IonGrid, IonLoading, IonModal, IonPage, IonRow, IonToast } from "@ionic/react";
import "./auth.css";
import { useTranslation } from "react-i18next";
import { Redirect } from "react-router";
import { useTenant } from "../../hooks/multiTenancy";
import { useAuth } from "../../hooks/auth";
import { getSalesPoint } from "../../utils";
import { useSelectedSalesPoint } from "../../hooks/selectedSalesPoint";
import Stores from "../main/stores";
import { useEffect, useState } from "react";
import { ApolloClient, InMemoryCache, NormalizedCacheObject, useQuery } from "@apollo/client";
import { reservationGuestWebsiteEnabledQuery } from "../../apollo-client/queries";

const Auth: React.FC = () => {
  const { t } = useTranslation();
  const tenant = useTenant();
  const [auth] = useAuth();
  const [isStoresModalOpen, setStoresModalOpen] = useState(false);
  const [showReservationUnavailable, setShowReservationUnavailable] = useState(false);
  const [selectedSalesPointId] = useSelectedSalesPoint();
  const [reservationsClient, setReservationsClient] = useState<ApolloClient<NormalizedCacheObject>>();
  const selectedSalesPoint = getSalesPoint(tenant, selectedSalesPointId);
  const tsReservationConfigured = selectedSalesPoint?.salesPoint.tsReservationConfigured;
  const registryItemId = selectedSalesPoint?.salesPoint.registryItemId;
  const tsReservationUrl = `${process.env.REACT_APP_RESERVATIONS_BASEURL}${registryItemId}`;

  useEffect(() => {
    const client = new ApolloClient({
      uri: `${process.env.REACT_APP_PROXY_BASEURL}/micro:reservations:api/graphql`,
      cache: new InMemoryCache(),
      headers: process.env.REACT_APP_RESERVATIONS_TOKEN ? { "Authorization": `Bearer ${process.env.REACT_APP_RESERVATIONS_TOKEN}` } : undefined,
    });
    setReservationsClient(client);
    return () => {
      client.stop();
    };
  }, []);

  const {data, loading, refetch} = useQuery<{ b2cSettings: { tenantId: string, propertyId: string }[] }>(reservationGuestWebsiteEnabledQuery, {
    client: reservationsClient,
    skip: !reservationsClient || !tsReservationConfigured || !registryItemId,
    variables: {
      pathOrRegistryItemId: registryItemId,
    },
  });

  const isGuestWebsiteEnabled = tsReservationConfigured && (data?.b2cSettings?.length ?? 0) > 0;

  const dismissStoresModal = async (selectedSalesPointId: number) => {
    setStoresModalOpen(false);
    const selectedSalesPoint = getSalesPoint(tenant, selectedSalesPointId);
    if (selectedSalesPoint?.salesPoint.tsReservationConfigured) {
      const registryItemId = selectedSalesPoint?.salesPoint.registryItemId;
      try {
        const { data } = await refetch({
          pathOrRegistryItemId: registryItemId,
        });
        if ((data?.b2cSettings?.length ?? 0) > 0) {
          window.open(`${process.env.REACT_APP_RESERVATIONS_BASEURL}${registryItemId}`)?.focus();
        } else {
          setShowReservationUnavailable(true);
        }
      } catch (e) {
        setShowReservationUnavailable(true);
      }
    } else {
      setShowReservationUnavailable(true);
    }
  };

  if (auth.authenticated) {
    return <Redirect to="/app/home" />;
  }

  return (
    <IonPage className="auth">
      <IonContent fullscreen>
        <IonLoading isOpen={loading} message={t("loading")} />
        <div className="background-image" style={{ backgroundImage: `url('${tenant.baseURL}/background.png')` }} />
        <div className="background-shadow" />
        <div className="content">
          <img className="logo" src={`${tenant.baseURL}/logo.png`} />
          <div className="tagline">
            <div>{tenant.settings?.fidelityAccount?.payoff || tenant.settings?.payoff}</div>
            <div>{tenant.settings?.payoff2}</div>
          </div>
          {!tenant.settings?.digitalPaymentOnly && (
            <>
              <div className="form auth-buttons">
                <IonGrid>
                  <IonRow>
                    <IonCol size="6">
                      <IonButton color="primary" routerLink="/register">
                        {t("auth.register")}
                      </IonButton>
                    </IonCol>
                    <IonCol size="6">
                      <IonButton color="primary" routerLink="/login">
                        {t("auth.login")}
                      </IonButton>
                    </IonCol>
                    <IonCol size="12">
                      <IonButton className="trial" routerLink="/app/products">
                        {t("auth.trial")}
                      </IonButton>
                    </IonCol>
                    <IonCol size="12">
                    {((!selectedSalesPoint && tenant.settings?.mobileCommerceSalesPoints?.some((p) => p.salesPoint.tsReservationConfigured)) || isGuestWebsiteEnabled) && (
                      <IonButton 
                        color="primary"
                        href={isGuestWebsiteEnabled ? tsReservationUrl : undefined}
                        target={isGuestWebsiteEnabled ? "_blank" : undefined}
                        onClick={!isGuestWebsiteEnabled ? () => setStoresModalOpen(true) : undefined}
                      >
                        {t("others.reservations")}
                      </IonButton>
                    )}
                    </IonCol>
                  </IonRow>
                </IonGrid>
              </div>
              <IonModal isOpen={isStoresModalOpen}>
                <Stores close={dismissStoresModal} />
              </IonModal>
              <IonToast
                isOpen={showReservationUnavailable}
                message={t("auth.reservations_unavailable")}
                onDidDismiss={() => setShowReservationUnavailable(false)}
                duration={2000}
              />
            </>
          )}
        </div>
      </IonContent>
    </IonPage>
  );
};

export default Auth;
